import {
  Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectorRef,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { AiCreditRequestContext, AiIntegrationsService } from 'src/app/core/services/ai-integrations.service';
import { AiWalletStateService } from 'src/app/core/services/ai-wallet-state.service';

export interface DocType {
  code: string;
  label: string;
  priority: 'important' | 'mandatory' | 'recommended' | 'optional';
  icon: string;
  description: string;
  fields: string[];
  selected: boolean;
  file?: File | null;
}

export interface DocExtractionResult {
  typology?: string;
  propertyType?: string;
  condition?: string;
  yearOfConstruction?: number;
  fraction?: string;
  floor?: string;
  doorNumber?: string;
  usefulArea?: number;
  grossArea?: number;
  grossPrivateArea?: number;
  grossDependentArea?: number;
  landArea?: number;
  buildingImplantationArea?: number;
  districtName?: string;
  countyName?: string;
  parishName?: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  zipCode?: string;
  ecCode?: string;
  ecNumber?: string;
  ecExpirationDate?: string;
  renewableEnergy?: number;
  carbonEmissions?: number;
  useLicense?: string;
  buildingLicense?: string;
  dateLicense?: string;
  technicalSheetLicense?: string;
  propertyValue?: number;
  price?: number;
  rooms?: number;
  bathrooms?: number;
  livingRooms?: number;
  offices?: number;
  parkingSpots?: number;
  suites?: number;
  divisions?: Array<{
    label?: string;
    count?: number;
    area?: number;
  }>;
  proximities?: Array<{
    label?: string;
    distance?: number;
    unit?: string;
  }>;
  characteristics?: string[];
  owners?: Array<{
    name?: string;
    fiscalIdentification?: string;
    email?: string;
    phone?: string;
    address?: string;
  }>;
  artigo?: string;
  confidence?: number;
  documentsProcessed?: string[];
  locationMatch?: any;
  structured?: Record<string, any>;
  form?: Record<string, any>;
  quality?: Record<string, any>;
  evidence?: any[];
  __aiDocuments?: Array<{
    code: string;
    label: string;
    file: File;
  }>;
}

const PROCESSING_PHASES = [
  'ai-doc-wizard.processing.verifying',
  'ai-doc-wizard.processing.extracting',
  'ai-doc-wizard.processing.structuring',
  'ai-doc-wizard.processing.validating',
];

@Component({
  selector: 'app-ai-doc-wizard',
  templateUrl: './ai-doc-wizard.component.html',
  styleUrls: ['./ai-doc-wizard.component.scss'],
})
export class AiDocWizardComponent implements OnInit, OnDestroy {
  @Input() dataToFormInit: any;
  @Input() startStep: number | null = null;
  @Output() extracted = new EventEmitter<DocExtractionResult>();
  @Output() skip = new EventEmitter<void>();

  step = 0;
  prevStep = 0;
  loading = false;
  error: string | null = null;
  processingPhase = 0;
  processingInterval: any;
  processingTimeout: any;
  revealedFields: Set<string> = new Set();
  revealInterval: any;
  extractedData: DocExtractionResult | null = null;
  creditsConsumed = 0;
  confidence = 0;
  documentsProcessed: string[] = [];
  docExtractionCost = 0;
  canRequestCreditTopUps = true;
  creditRequestContext: AiCreditRequestContext | null = null;

  readonly MAX_DOCS = 5;
  readonly MAX_SIZE_MB = 8;
  private readonly DOC_EXTRACTION_CODES = [
    'DOC_INTELLIGENCE_PROPERTY',
    'DOC_EXTRACTION',
    'DOCUMENT_EXTRACTION',
  ];

  docTypes: DocType[] = [
    { code: 'CADERNETA_PREDIAL', label: 'Caderneta Predial Urbana', priority: 'important', icon: 'ri-file-list-3-line', description: 'Localização, tipologia, áreas e ano de construção', fields: ['parish', 'typology', 'areas', 'year'], selected: true, file: null },
    { code: 'FICHA_TECNICA', label: 'Ficha Técnica de Habitação', priority: 'recommended', icon: 'ri-building-2-line', description: 'Divisões, casas de banho, materiais e acabamentos', fields: ['rooms', 'bathrooms', 'materials'], selected: false, file: null },
    { code: 'PLANTA_IMOVEL', label: 'Planta do Imóvel', priority: 'recommended', icon: 'ri-layout-2-line', description: 'Divisões, áreas por compartimento e distribuição do imóvel', fields: ['divisions', 'areas', 'characteristics'], selected: false, file: null },
    { code: 'CERTIFICADO_ENERGETICO', label: 'Certificado Energético', priority: 'recommended', icon: 'ri-leaf-line', description: 'Classe energética e emissões de CO2', fields: ['energy_class', 'co2'], selected: false, file: null },
    { code: 'LICENCA_UTILIZACAO', label: 'Licença de Utilização', priority: 'optional', icon: 'ri-government-line', description: 'Morada oficial, fração e uso permitido', fields: ['address', 'fraction', 'use'], selected: false, file: null },
    { code: 'CERTIDAO_PERMANENTE', label: 'Certidão Permanente', priority: 'optional', icon: 'ri-shield-check-line', description: 'Proprietário e ónus registados', fields: ['owner'], selected: false, file: null },
  ];

  readonly resultFields = [
    { key: 'typology', label: 'ai-doc-wizard.fields.typology', icon: 'ri-home-4-line', color: 'primary' },
    { key: 'yearOfConstruction', label: 'ai-doc-wizard.fields.year', icon: 'ri-calendar-line', color: 'secondary' },
    { key: 'parishName', label: 'ai-doc-wizard.fields.parish', icon: 'ri-map-pin-line', color: 'success' },
    { key: 'countyName', label: 'ai-doc-wizard.fields.county', icon: 'ri-map-2-line', color: 'success' },
    { key: 'districtName', label: 'ai-doc-wizard.fields.district', icon: 'ri-map-2-line', color: 'success' },
    { key: 'address', label: 'ai-doc-wizard.fields.address', icon: 'ri-road-map-line', color: 'info' },
    { key: 'zipCode', label: 'ai-doc-wizard.fields.zipCode', icon: 'ri-mail-line', color: 'info' },
    { key: 'floor', label: 'ai-doc-wizard.fields.floor', icon: 'ri-stack-line', color: 'warning' },
    { key: 'fraction', label: 'ai-doc-wizard.fields.fraction', icon: 'ri-layout-grid-line', color: 'warning' },
    { key: 'grossPrivateArea', label: 'ai-doc-wizard.fields.privateArea', icon: 'ri-shape-line', color: 'danger' },
    { key: 'grossDependentArea', label: 'ai-doc-wizard.fields.dependentArea', icon: 'ri-shape-2-line', color: 'danger' },
    { key: 'grossArea', label: 'ai-doc-wizard.fields.grossArea', icon: 'ri-layout-masonry-line', color: 'danger' },
    { key: 'rooms', label: 'ai-doc-wizard.fields.rooms', icon: 'ri-door-open-line', color: 'primary' },
    { key: 'bathrooms', label: 'ai-doc-wizard.fields.bathrooms', icon: 'ri-drop-line', color: 'primary' },
    { key: 'ecCode', label: 'ai-doc-wizard.fields.energyClass', icon: 'ri-leaf-line', color: 'success' },
  ];

  constructor(
    private aiService: AiIntegrationsService,
    private cdr: ChangeDetectorRef,
    private aiWalletState: AiWalletStateService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadDocExtractionCost();
    this.loadCreditRequestContext();
    if (this.startStep !== null && this.startStep !== undefined) {
      this.step = this.startStep;
      this.prevStep = this.startStep;
    }
  }

  private loadCreditRequestContext() {
    this.aiService.getCreditRequestContext().subscribe({
      next: (ctx) => {
        this.creditRequestContext = ctx;
        this.canRequestCreditTopUps = ctx?.canRequestCredits ?? true;
      },
      error: () => {
        this.creditRequestContext = null;
        this.canRequestCreditTopUps = true;
      },
    });
  }

  private loadDocExtractionCost() {
    this.aiService.getServicesWithStatus().subscribe({
      next: (services) => {
        for (const code of this.DOC_EXTRACTION_CODES) {
          const service = (services ?? []).find(
            (item: any) =>
              String(item?.code ?? '').toUpperCase() === code,
          );
          if (service) {
            const effectiveCost = Number(service?.effectiveCreditCost);
            if (Number.isFinite(effectiveCost)) {
              this.docExtractionCost = Math.max(0, effectiveCost);
              break;
            }
          }
          if (service && Number.isFinite(service?.defaultCreditCost)) {
            this.docExtractionCost = Math.max(0, Number(service.defaultCreditCost));
            break;
          }
        }
      },
      error: () => {
        this.docExtractionCost = 0;
      },
    });
  }

  get slideDir(): 1 | -1 { return this.step >= this.prevStep ? 1 : -1; }

  setStep(next: number) {
    this.prevStep = this.step;
    this.step = next;
  }

  get selectedCount(): number { return this.docTypes.filter((d) => d.selected).length; }
  get selectedDocs(): DocType[] { return this.docTypes.filter((d) => d.selected); }
  get selectedWithFileCount(): number { return this.docTypes.filter((d) => d.selected && !!d.file).length; }
  get canExtract(): boolean {
    return this.selectedCount > 0
      && this.selectedCount <= this.MAX_DOCS
      && this.selectedWithFileCount === this.selectedCount;
  }

  priorityColor(p: string): string {
    const map: Record<string, string> = {
      important: 'danger',
      mandatory: 'danger',
      recommended: 'warning',
      optional: 'secondary',
    };
    return map[p] ?? 'secondary';
  }

  priorityLabel(priority: DocType['priority']): string {
    if (priority === 'important' || priority === 'mandatory') {
      return this.translateWithFallback('ai-doc-wizard.priority.important', 'Importante');
    }
    if (priority === 'recommended') {
      return this.translateWithFallback('ai-doc-wizard.priority.recommended', 'Recomendado');
    }
    return this.translateWithFallback('ai-doc-wizard.priority.optional', 'Opcional');
  }

  toggleDoc(doc: DocType) {
    if (!doc.selected && this.selectedCount >= this.MAX_DOCS) return;
    doc.selected = !doc.selected;
  }

  onFileChange(event: Event, doc: DocType) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (file.size > this.MAX_SIZE_MB * 1024 * 1024) {
      this.openGenericError(this.translateService.instant('ai-feedback.fileTooLarge', { size: this.MAX_SIZE_MB }));
      return;
    }
    doc.file = file;
    doc.selected = true;
    this.error = null;
  }

  clearDocFile(doc: DocType) {
    doc.file = null;
  }

  chooseAi() { this.setStep(1); }
  chooseTraditional() { this.skip.emit(); }
  backToChoice() { this.setStep(this.startStep ?? 0); }

  private resolveErrorPayload(payload: any): { message: string; code: string } {
    const code = String(
      payload?.errorCode ??
      payload?.error?.errorCode ??
      payload?.code ??
      '',
    ).toUpperCase();

    const message =
      payload?.errorMessage ??
      payload?.error?.errorMessage ??
      payload?.error?.message ??
      payload?.message ??
      this.translateService.instant('ai-feedback.genericErrorBody');

    return { code, message };
  }

  private isInsufficientCreditsError(payload: any): boolean {
    const { code, message } = this.resolveErrorPayload(payload);
    const normalized = String(message).toLowerCase();
    return (
      code.includes('INSUFFICIENT') ||
      code.includes('BALANCE') ||
      normalized.includes('sem créditos') ||
      normalized.includes('saldo insuficiente') ||
      normalized.includes('insufficient credit')
    );
  }

  private openGenericError(message: string) {
    Swal.fire({
      icon: 'error',
      title: this.translateService.instant('ai-feedback.genericErrorTitle'),
      text: message || this.translateService.instant('ai-feedback.genericErrorBody'),
      confirmButtonText: this.translateService.instant('t-close'),
      confirmButtonColor: 'rgb(54, 69, 116)',
    });
  }

  private translateWithFallback(key: string, fallback: string): string {
    const translated = this.translateService.instant(key);
    if (!translated || translated === key) return fallback;
    return translated;
  }

  private openInsufficientCreditsFlow() {
    const canRequestTopUps = this.canRequestCreditTopUps;
    Swal.fire({
      icon: 'warning',
      title: this.translateService.instant('ai-feedback.insufficientCreditsTitle'),
      html: `
        <p class="mb-2">${this.translateService.instant('ai-feedback.insufficientCreditsBody')}</p>
        <small class="text-muted">${this.translateService.instant('ai-feedback.insufficientCreditsHint')}</small>
      `,
      showCancelButton: canRequestTopUps,
      confirmButtonText: canRequestTopUps
        ? this.translateService.instant('ai-feedback.requestCreditsAction')
        : this.translateService.instant('t-close'),
      cancelButtonText: this.translateService.instant('t-cancel'),
      confirmButtonColor: 'rgb(64, 81, 137)',
      cancelButtonColor: '#878a99',
    }).then((res) => {
      if (res.isConfirmed && canRequestTopUps) this.promptCreditRequest();
    });
  }

  private promptCreditRequest() {
    if (!this.canRequestCreditTopUps) {
      this.openGenericError(this.translateService.instant('ai-feedback.requestCreditsNotAllowed'));
      return;
    }

    Swal.fire({
      title: this.translateService.instant('ai-feedback.requestCreditsModalTitle'),
      input: 'number',
      inputValue: 50,
      inputAttributes: { min: '1', step: '1' },
      showCancelButton: true,
      confirmButtonText: this.translateService.instant('ai-feedback.requestCreditsSubmit'),
      cancelButtonText: this.translateService.instant('t-cancel'),
      confirmButtonColor: 'rgb(64, 81, 137)',
      preConfirm: (value) => {
        const credits = Number(value);
        if (!Number.isFinite(credits) || credits < 1) {
          Swal.showValidationMessage(this.translateService.instant('ai-feedback.requestCreditsValidation'));
          return null;
        }
        return credits;
      },
    }).then((res) => {
      if (!res.isConfirmed || !res.value) return;
      this.aiService.createCreditRequest({
        creditsRequested: Number(res.value),
        reason: this.translateService.instant('ai-feedback.requestCreditsReason', {
          context: this.translateService.instant('ai-feedback.context.docExtraction'),
        }),
      }).subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: this.translateService.instant('ai-feedback.requestCreditsSuccessTitle'),
            text: this.translateService.instant('ai-feedback.requestCreditsSuccessBody'),
            confirmButtonText: this.translateService.instant('t-close'),
            confirmButtonColor: 'rgb(54, 69, 116)',
          });
        },
        error: (err: any) => {
          const { message } = this.resolveErrorPayload(err);
          this.openGenericError(message);
        },
      });
    });
  }

  private handleExtractError(payload: any) {
    if (this.isInsufficientCreditsError(payload)) {
      this.openInsufficientCreditsFlow();
      return;
    }
    const { message } = this.resolveErrorPayload(payload);
    this.openGenericError(message);
  }

  private formatCreditAmount(value: number | null): string {
    if (value === null || !Number.isFinite(value)) {
      return this.translateService.instant('ai-feedback.creditConfirm.balanceUnavailable');
    }
    return `${Math.max(0, Math.floor(value)).toLocaleString('pt-PT')} ${this.translateService.instant('ai-feedback.creditConfirm.creditsLabel')}`;
  }

  private getCurrentWalletBalance(): number | null {
    const value = this.aiWalletState.balance;
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    return Math.max(0, value);
  }

  private async confirmExtractionConsumption(): Promise<boolean> {
    const cost = Math.max(0, Number(this.docExtractionCost || 0));
    const currentBalance = this.getCurrentWalletBalance();
    const projectedBalance =
      currentBalance === null ? null : currentBalance - cost;
    const insufficient =
      projectedBalance !== null && projectedBalance < 0;
    const canRequestTopUps = this.canRequestCreditTopUps;

    const result = await Swal.fire({
      title: this.translateService.instant('ai-feedback.creditConfirm.title'),
      text: this.translateService.instant('ai-feedback.creditConfirm.subtitle'),
      html: `
        <div class="ai-credit-cost-modal">
          <div class="ai-credit-cost-section">
            <div class="ai-credit-cost-section__title">${this.translateService.instant('ai-feedback.creditConfirm.selectedServiceLabel')}</div>
            <div class="ai-credit-service-card">
              <div class="ai-credit-service-card__icon"><i class="ri-file-search-line"></i></div>
              <div class="ai-credit-service-card__content">
                <strong>${this.translateService.instant('ai-feedback.creditConfirm.services.docExtraction.name')}</strong>
                <small>${this.translateService.instant('ai-feedback.creditConfirm.services.docExtraction.description')}</small>
              </div>
              <div class="ai-credit-service-card__cost">
                <strong>${cost}</strong>
                <span>${this.translateService.instant('ai-feedback.creditConfirm.creditsLabel')}</span>
              </div>
            </div>
          </div>
          <div class="ai-credit-cost-section">
            <div class="ai-credit-cost-section__title">${this.translateService.instant('ai-feedback.creditConfirm.summaryLabel')}</div>
            <div class="ai-credit-confirm__ledger">
              <div class="ai-credit-confirm__row">
                <span>${this.translateService.instant('ai-feedback.creditConfirm.currentBalance')}</span>
                <strong>${this.formatCreditAmount(currentBalance)}</strong>
              </div>
              <div class="ai-credit-confirm__row">
                <span>${this.translateService.instant('ai-feedback.creditConfirm.cost')}</span>
                <strong class="is-cost">-${cost.toLocaleString('pt-PT')} ${this.translateService.instant('ai-feedback.creditConfirm.creditsLabel')}</strong>
              </div>
              <div class="ai-credit-confirm__row ai-credit-confirm__row--result">
                <span>${this.translateService.instant('ai-feedback.creditConfirm.afterBalance')}</span>
                <strong class="${insufficient ? 'is-danger' : 'is-ok'}">${this.formatCreditAmount(projectedBalance)}</strong>
              </div>
            </div>
            <small class="ai-credit-confirm__hint-info">${this.translateService.instant('ai-feedback.creditConfirm.afterHint')}</small>
            ${insufficient ? `<small class="ai-credit-confirm__hint">${this.translateService.instant('ai-feedback.creditConfirm.insufficientHint')}</small>` : ''}
          </div>
        </div>
      `,
      showCancelButton: !insufficient || canRequestTopUps,
      showCloseButton: true,
      confirmButtonText: insufficient
        ? canRequestTopUps
          ? this.translateService.instant('ai-feedback.requestCreditsAction')
          : this.translateService.instant('t-close')
        : this.translateService.instant('ai-feedback.creditConfirm.confirmWithCost', { cost }),
      cancelButtonText: this.translateService.instant('t-cancel'),
      confirmButtonColor: insufficient ? '#f06548' : 'rgb(64, 81, 137)',
      cancelButtonColor: '#878a99',
      customClass: {
        popup: 'ai-credit-confirm-popup',
        confirmButton: 'ai-credit-confirm-btn ai-credit-confirm-btn--confirm',
        cancelButton: 'ai-credit-confirm-btn ai-credit-confirm-btn--cancel',
      },
      buttonsStyling: false,
      preConfirm: () =>
        new Promise((resolve) => {
          const popup = Swal.getPopup();
          popup?.classList.add('ai-credit-confirm-popup--committing');
          setTimeout(() => resolve(true), 450);
        }),
    });

    if (!result.isConfirmed) return false;
    if (insufficient) {
      if (canRequestTopUps) {
        this.promptCreditRequest();
      } else {
        this.openGenericError(this.translateService.instant('ai-feedback.requestCreditsNotAllowed'));
      }
      return false;
    }
    return true;
  }

  async extract() {
    if (this.selectedCount === 0) {
      this.openGenericError(
        this.translateWithFallback(
          'ai-doc-wizard.upload.validation.selectDocumentType',
          'Selecione pelo menos um tipo de documento.',
        ),
      );
      return;
    }

    if (this.selectedWithFileCount !== this.selectedCount) {
      this.openGenericError(
        this.translateWithFallback(
          'ai-doc-wizard.upload.validation.attachAllSelected',
          'Anexe ficheiro(s) para todos os documentos selecionados antes de extrair.',
        ),
      );
      return;
    }

    if (!this.canExtract) return;
    if (!(await this.confirmExtractionConsumption())) return;
    this.setStep(2);
    this.loading = true;
    this.startProcessingTimeline();

    const selectedDocs = this.docTypes.filter((d) => d.selected && !!d.file);
    const selectedCodes = selectedDocs.map((d) => d.code);
    const files = selectedDocs.map((d) => d.file as File);
    const inputData = JSON.stringify({
      documentTypes: selectedCodes,
      attachments: selectedDocs.map((doc) => ({
        code: doc.code,
        fileName: doc.file?.name,
        mimeType: doc.file?.type,
      })),
    });
    const idempotencyKey = `doc-upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    this.aiService.executeDocIntelligenceUpload(idempotencyKey, files, undefined, inputData).subscribe({
      next: (result) => {
        this.finalizeExtractionResult(result);
      },
      error: (err: any) => {
        if (this.isGatewayTimeoutError(err)) {
          this.recoverExtractionAfterTimeout(idempotencyKey);
          return;
        }
        this.stopProcessingTimeline();
        this.loading = false;
        this.setStep(1);
        this.handleExtractError(err);
        this.cdr.detectChanges();
      },
    });
  }

  private isGatewayTimeoutError(err: any): boolean {
    const status = Number(err?.status ?? err?.error?.status ?? 0);
    if (status === 504) return true;
    const text = String(
      err?.error?.message ??
      err?.message ??
      '',
    ).toLowerCase();
    return text.includes('gateway timeout') || text.includes('504');
  }

  private recoverExtractionAfterTimeout(idempotencyKey: string) {
    this.aiService
      .waitForUsageResultByIdempotency(idempotencyKey, {
        maxAttempts: 24,
        intervalMs: 2500,
      })
      .subscribe({
        next: (result) => this.finalizeExtractionResult(result as any),
        error: (err: any) => {
          this.stopProcessingTimeline();
          this.loading = false;
          this.setStep(1);
          this.handleExtractError(err);
          this.cdr.detectChanges();
        },
      });
  }

  private finalizeExtractionResult(result: any) {
    this.stopProcessingTimeline();
    this.loading = false;

    if (result?.ok && result?.output && result?.output['extraction']) {
      const output = result.output ?? {};
      const extraction = output['extraction'] as DocExtractionResult;
      this.extractedData = {
        ...extraction,
        locationMatch: output?.["locationMatch"],
        structured: output?.["structured"],
        form: output?.["form"],
        quality: output?.["quality"] ?? output?.["structured"]?.quality,
        evidence: output?.["evidence"] ?? output?.["structured"]?.evidence,
        documentsProcessed:
          output?.["documentsProcessed"] ?? (extraction as any).documentsProcessed,
        confidence: output?.["confidence"] ?? (extraction as any).confidence,
      };
      this.creditsConsumed = result.creditsConsumed ?? 0;
      this.confidence = (this.extractedData as any).confidence ?? 0;
      this.documentsProcessed =
        (this.extractedData as any).documentsProcessed ?? [];
      this.aiWalletState.applyConsumption((result as any).remainingBalance, this.creditsConsumed);
      this.setStep(3);
      this.animateResultFields();
    } else {
      this.setStep(1);
      this.handleExtractError(result);
    }

    this.cdr.detectChanges();
  }

  private animateResultFields() {
    this.revealedFields.clear();
    let i = 0;
    this.revealInterval = setInterval(() => {
      if (i < this.resultFields.length) {
        const field = this.resultFields[i];
        if (this.extractedData && (this.extractedData as any)[field.key] !== undefined) {
          this.revealedFields.add(field.key);
          this.cdr.detectChanges();
        }
        i++;
      } else {
        clearInterval(this.revealInterval);
      }
    }, 150);
  }

  getFieldValue(key: string): any { return this.extractedData ? (this.extractedData as any)[key] : null; }
  isRevealed(key: string): boolean { return this.revealedFields.has(key); }
  get processingPhasKey(): string { return PROCESSING_PHASES[this.processingPhase]; }
  get processingPhasesList(): string[] { return PROCESSING_PHASES; }

  rejectedFields: Set<string> = new Set();

  isFieldAccepted(key: string): boolean { return !this.rejectedFields.has(key); }

  toggleFieldAccept(key: string) {
    if (this.rejectedFields.has(key)) this.rejectedFields.delete(key);
    else this.rejectedFields.add(key);
  }

  acceptField(key: string) {
    this.rejectedFields.delete(key);
  }

  rejectField(key: string) {
    this.rejectedFields.add(key);
  }

  acceptAllFields() {
    this.rejectedFields.clear();
  }

  rejectAllFields() {
    if (!this.extractedData) return;
    this.resultFields.forEach((f) => {
      const v = (this.extractedData as any)[f.key];
      if (v !== undefined && v !== null) this.rejectedFields.add(f.key);
    });
  }

  get acceptedCount(): number {
    if (!this.extractedData) return 0;
    return this.resultFields.filter((f) => {
      const v = (this.extractedData as any)[f.key];
      return v !== undefined && v !== null && !this.rejectedFields.has(f.key);
    }).length;
  }

  get totalExtractedCount(): number {
    if (!this.extractedData) return 0;
    return this.resultFields.filter((f) => {
      const v = (this.extractedData as any)[f.key];
      return v !== undefined && v !== null;
    }).length;
  }

  get ignoredCount(): number { return this.totalExtractedCount - this.acceptedCount; }

  confirm() {
    if (!this.extractedData) return;
    const filtered: any = { ...this.extractedData };
    this.rejectedFields.forEach((k) => {
      this.clearRejectedExtractionField(filtered, k);
    });
    filtered.__aiDocuments = this.selectedDocs
      .filter((doc) => !!doc.file)
      .map((doc) => ({
        code: doc.code,
        label: doc.label,
        file: doc.file as File,
      }));
    this.extracted.emit(filtered);
  }

  private clearRejectedExtractionField(payload: any, key: string) {
    delete payload[key];

    const paths: Record<string, string[][]> = {
      typology: [
        ["propertyType"],
        ["form", "details", "propertyType"],
        ["form", "details", "typology"],
      ],
      yearOfConstruction: [["form", "details", "yearOfConstruction"]],
      parishName: [
        ["form", "address", "parishName"],
        ["structured", "address", "parishName"],
      ],
      countyName: [
        ["form", "address", "countyName"],
        ["structured", "address", "countyName"],
      ],
      districtName: [
        ["form", "address", "districtName"],
        ["structured", "address", "districtName"],
      ],
      address: [
        ["form", "address", "address"],
        ["structured", "address", "address"],
      ],
      zipCode: [
        ["form", "address", "zipCode"],
        ["structured", "address", "zipCode"],
      ],
      floor: [
        ["form", "address", "floor"],
        ["structured", "address", "floor"],
      ],
      fraction: [
        ["form", "address", "fraction"],
        ["structured", "address", "fraction"],
      ],
      grossPrivateArea: [
        ["form", "areasNearby", "grossPrivateArea"],
        ["structured", "areas", "grossPrivateArea"],
      ],
      grossDependentArea: [
        ["form", "areasNearby", "grossDependentArea"],
        ["structured", "areas", "grossDependentArea"],
      ],
      grossArea: [
        ["form", "areasNearby", "grossArea"],
        ["structured", "areas", "grossArea"],
      ],
      rooms: [
        ["form", "rooms", "rooms"],
        ["structured", "rooms", "rooms"],
      ],
      bathrooms: [
        ["form", "rooms", "bathrooms"],
        ["structured", "rooms", "bathrooms"],
      ],
      ecCode: [
        ["form", "details", "ecCode"],
        ["structured", "energy", "classCode"],
      ],
    };

    (paths[key] ?? []).forEach((path) => this.deleteNestedValue(payload, path));
  }

  private deleteNestedValue(target: any, path: string[]) {
    const leaf = path[path.length - 1];
    if (!leaf) return;

    const parent = path
      .slice(0, -1)
      .reduce((current, segment) => current?.[segment], target);
    if (parent && typeof parent === "object") {
      delete parent[leaf];
    }
  }

  private startProcessingTimeline() {
    this.stopProcessingTimeline();
    this.processingPhase = 0;

    const nextPhase = () => {
      if (this.processingPhase < PROCESSING_PHASES.length - 1) {
        this.processingPhase += 1;
        this.cdr.detectChanges();
        this.processingTimeout = setTimeout(nextPhase, 1200);
      }
    };

    this.processingTimeout = setTimeout(nextPhase, 1000);
  }

  private stopProcessingTimeline() {
    clearInterval(this.processingInterval);
    clearTimeout(this.processingTimeout);
  }

  ngOnDestroy() {
    this.stopProcessingTimeline();
    clearInterval(this.revealInterval);
  }
}
